import React, { useState, useEffect, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import { API_get_timecard_hours_split } from "../../../../../constants";
import { getRightworkAPI } from "../../../requests/utilities/requests";
import { getBusinessWeekStartDayIndex } from "../../dateRangePresets";
import WeekSelector from "../../../home/top_bar_components/WeekSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import ReportCsvDownloadButton from "../ReportCsvDownloadButton";
import ReportEmailToggle from "../ReportEmailToggle";
import {
  displayName,
  formatCurrency,
  formatHours,
} from "./_shared/laborMath";
import { lastCompletedBusinessWeekStart } from "./_shared/reportDates";

// Pay multipliers match the Daily Punch Audit / Paylocity export.
const PAY_MULTIPLIER = {
  regular: 1,
  overtime: 1.5,
  doubletime: 2,
  holiday: 1,
};

function snapToWeekStart(date, weekStartDayIndex) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = (d.getDay() - weekStartDayIndex + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function payFor(hours, wage, kind) {
  if (hours == null || wage == null) return 0;
  const h = Number(hours);
  const w = Number(wage);
  if (!Number.isFinite(h) || !Number.isFinite(w) || w <= 0) return 0;
  return h * w * (PAY_MULTIPLIER[kind] ?? 1);
}

function emptyEmployeeRow(stm) {
  return {
    id: stm?.id ?? null,
    employee: displayName(stm),
    // Prefer the configured payroll-system ID (UserCompanyProfile
    // .payroll_external_id) so the column reconciles against the
    // customer's payroll system; fall back to the internal STM id only
    // when no external id is on file.
    payrollId: stm?.payroll_external_id || stm?.id || "—",
    // displayedWage is the first non-null wage we see for this employee.
    // Per-shift pay math is always correct (each shift uses its own wage);
    // this is just the value rendered in the "Rate" column. If the employee
    // worked at multiple distinct rates within the week, wageMixed flips
    // true and the row surfaces a "mixed" badge with a tooltip explaining
    // the per-shift correctness.
    displayedWage: null,
    wageMixed: false,
    reg: 0,
    ot: 0,
    dt: 0,
    hol: 0,
    mbpHrs: 0,
    sspHrs: 0,
    regPay: 0,
    otPay: 0,
    dtPay: 0,
    holPay: 0,
    specialPay: 0,
    totalHrs: 0,
    totalPay: 0,
  };
}

function NotAvailable() {
  return <span className="text-slate-300">—</span>;
}

export default function PayrollReport({
  report,
  onBack,
  storePicker,
  homeStore,
  storeNames,
  userId,
}) {
  const weekStartDayIndex = useMemo(
    () => getBusinessWeekStartDayIndex(storeNames, homeStore),
    [storeNames, homeStore]
  );

  // Opens on the store's last full business week, the one payroll is run for.
  const [weekStart, setWeekStart] = useState(() =>
    lastCompletedBusinessWeekStart(storeNames, homeStore, weekStartDayIndex)
  );

  useEffect(() => {
    setWeekStart((prev) => snapToWeekStart(prev, weekStartDayIndex));
  }, [weekStartDayIndex]);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + 6);
    return d;
  }, [weekStart]);

  const incrementWeek = useCallback((currentStart, direction) => {
    const next = new Date(currentStart);
    next.setDate(next.getDate() + direction * 7);
    setWeekStart(next);
  }, []);

  const setWeekStartDate = useCallback(
    (newDate) => setWeekStart(snapToWeekStart(newDate, weekStartDayIndex)),
    [weekStartDayIndex]
  );

  const startStr = useMemo(
    () => dayjs(weekStart).format("YYYY-MM-DD"),
    [weekStart]
  );
  const endStr = useMemo(() => dayjs(weekEnd).format("YYYY-MM-DD"), [weekEnd]);

  const [timecards, setTimecards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!homeStore) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await getRightworkAPI(API_get_timecard_hours_split, {
          store_id: homeStore,
          date_start: startStr,
          date_end: endStr,
        });
        if (cancelled) return;
        const list = Array.isArray(res?.data?.timecards)
          ? res.data.timecards
          : [];
        setTimecards(list);
      } catch (e) {
        if (cancelled) return;
        setError(
          "Couldn't load payroll data. Try again or pick another week."
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [homeStore, startStr, endStr, retryNonce]);

  const handleRetry = useCallback(() => setRetryNonce((n) => n + 1), []);

  // Roll timecards up per employee. Pay math is per-shift (using each
  // shift's role-specific wage), then summed. Matches the Paylocity export's
  // approach — different role-specific rates within a week produce
  // role-correct pay totals, but the "Rate" display flags the mixed case.
  const rows = useMemo(() => {
    const byEmployee = new Map();

    // Synthetic key for POS punches that couldn't be matched to an STM
    // during ingest. We keep them in the rollup so the weekly hours
    // total reflects real labor; the row renders as "Unmatched (POS)"
    // and pay is left "—" since we have no wage to apply.
    const UNMATCHED_KEY = "__unmatched_pos__";

    for (const tc of timecards) {
      const stm = tc.store_team_member;
      const isUnmatched = !stm?.id;
      const employeeKey = isUnmatched ? UNMATCHED_KEY : stm.id;

      const wageRaw =
        tc.effective_hourly_wage != null && tc.effective_hourly_wage !== ""
          ? Number(tc.effective_hourly_wage)
          : NaN;
      const wage = Number.isFinite(wageRaw) && wageRaw > 0 ? wageRaw : null;

      const reg = Number(tc.regular ?? 0);
      const ot = Number(tc.overtime ?? 0);
      const dt = Number(tc.doubletime ?? 0);
      const hol = Number(tc.holiday ?? 0);
      const mbp = Number(tc.meal_break_premium_hours ?? 0) || 0;
      const ssp = Number(tc.split_shift_premium_hours ?? 0) || 0;

      const regPay = payFor(reg, wage, "regular");
      const otPay = payFor(ot, wage, "overtime");
      const dtPay = payFor(dt, wage, "doubletime");
      const holPay = payFor(hol, wage, "holiday");
      // MBP is paid at the regular rate (CA Labor Code 226.7). SSP is paid
      // at minimum wage per the CA wage order — we don't have min-wage data
      // in the system, so SSP $ is deliberately omitted from Special Pay
      // and Total Pay to avoid overstating. SSP hours stay visible in the
      // SSP Hrs column.
      const mbpPay = wage != null && mbp > 0 ? mbp * wage : 0;
      const specialPay = mbpPay;

      let row = byEmployee.get(employeeKey);
      if (!row) {
        row = isUnmatched
          ? {
              ...emptyEmployeeRow(null),
              id: UNMATCHED_KEY,
              employee: "Unmatched (POS)",
              payrollId: "—",
            }
          : emptyEmployeeRow(stm);
        byEmployee.set(employeeKey, row);
      }

      // Lock in the first non-null wage we see; flip wageMixed if any
      // subsequent shift carries a different rate. Pay totals are still
      // correct per-shift; this just drives the Rate column's display.
      if (wage != null) {
        if (row.displayedWage == null) row.displayedWage = wage;
        else if (row.displayedWage !== wage) row.wageMixed = true;
      }

      row.reg += reg;
      row.ot += ot;
      row.dt += dt;
      row.hol += hol;
      row.mbpHrs += mbp;
      row.sspHrs += ssp;
      row.regPay += regPay;
      row.otPay += otPay;
      row.dtPay += dtPay;
      row.holPay += holPay;
      row.specialPay += specialPay;
      row.totalHrs += Number(tc.total_paid_hours ?? reg + ot + dt + hol);
      row.totalPay += regPay + otPay + dtPay + holPay + specialPay;
    }

    return Array.from(byEmployee.values()).sort((a, b) =>
      a.employee.localeCompare(b.employee)
    );
  }, [timecards]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.reg += r.reg;
        acc.ot += r.ot;
        acc.dt += r.dt;
        acc.hol += r.hol;
        acc.mbpHrs += r.mbpHrs;
        acc.sspHrs += r.sspHrs;
        acc.regPay += r.regPay;
        acc.otPay += r.otPay;
        acc.dtPay += r.dtPay;
        acc.holPay += r.holPay;
        acc.specialPay += r.specialPay;
        acc.totalHrs += r.totalHrs;
        acc.totalPay += r.totalPay;
        return acc;
      },
      {
        reg: 0,
        ot: 0,
        dt: 0,
        hol: 0,
        mbpHrs: 0,
        sspHrs: 0,
        regPay: 0,
        otPay: 0,
        dtPay: 0,
        holPay: 0,
        specialPay: 0,
        totalHrs: 0,
        totalPay: 0,
      }
    );
  }, [rows]);

  const filters = (
    <div className="flex items-center">
      <WeekSelector
        start_date={weekStart}
        end_date={weekEnd}
        increment_week={incrementWeek}
        set_week_start_date={setWeekStartDate}
        businessWeekStartDayIndex={weekStartDayIndex}
      />
    </div>
  );

  const weekLabel = `${dayjs(weekStart).format("MMM D")} – ${dayjs(
    weekEnd
  ).format("MMM D, YYYY")}`;

  return (
    <PrebuiltReportShell
      actions={
        <>
          <ReportEmailToggle
            reportId={report.id}
            userId={userId}
            schedule={report.emailSchedule}
          />
          <ReportCsvDownloadButton
            reportId="payroll-report"
            storeId={homeStore}
            start={dayjs(weekStart).format("YYYY-MM-DD")}
            end={dayjs(weekEnd).format("YYYY-MM-DD")}
          />
        </>
      }
      report={report}
      onBack={onBack}
      filters={filters}
      storePicker={storePicker}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={handleRetry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-500 py-8 text-center border border-slate-200 rounded-lg">
          No paid hours for this week.
        </div>
      ) : (
        <>
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-slate-700">{weekLabel}</h4>
            <p className="text-xs text-slate-500">
              Per-employee weekly rollup. Pay uses each shift's role-specific
              wage (general-wage fallback) at FLSA multipliers (Reg 1×,
              OT 1.5×, DT 2×, Hol 1×), plus Meal Break Premiums at the
              regular rate. Split Shift Premium hours are surfaced in the
              SSP Hrs column but their $ is excluded — CA pays SSP at
              minimum wage, so check your payroll system for the SSP amount.
              Verify tips and FLSA blended-rate adjustments for multi-role
              workweeks in payroll.
            </p>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-700">
                  <tr>
                    <th className="text-left font-semibold px-3 py-2 sticky left-0 bg-slate-50 z-10 min-w-[180px]">
                      Employee
                    </th>
                    <th className="text-left font-semibold px-3 py-2">
                      Payroll ID
                    </th>
                    <th className="text-right font-semibold px-3 py-2">Rate</th>
                    <th className="text-right font-semibold px-3 py-2">Reg Hrs</th>
                    <th className="text-right font-semibold px-3 py-2">OT Hrs</th>
                    <th className="text-right font-semibold px-3 py-2">DT Hrs</th>
                    <th className="text-right font-semibold px-3 py-2">Hol Hrs</th>
                    <th className="text-right font-semibold px-3 py-2">MBP Hrs</th>
                    <th className="text-right font-semibold px-3 py-2">SSP Hrs</th>
                    <th className="text-right font-semibold px-3 py-2">Total Hrs</th>
                    <th className="text-right font-semibold px-3 py-2">Reg $</th>
                    <th className="text-right font-semibold px-3 py-2">OT $</th>
                    <th className="text-right font-semibold px-3 py-2">DT $</th>
                    <th className="text-right font-semibold px-3 py-2">Hol $</th>
                    <th className="text-right font-semibold px-3 py-2">Special $</th>
                    <th className="text-right font-semibold px-3 py-2">Total Pay</th>
                    <th className="text-right font-semibold px-3 py-2">Tips</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className="group border-b border-slate-100 hover:bg-slate-50"
                    >
                      <td className="px-3 py-2 font-medium text-slate-800 sticky left-0 z-10 bg-white group-hover:bg-slate-50">
                        {r.employee}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{r.payrollId}</td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {r.displayedWage == null
                          ? "—"
                          : (
                              <span
                                title={
                                  r.wageMixed
                                    ? "Employee worked at multiple role-specific rates this week; per-shift pay is correct but this displays only the most recent rate."
                                    : undefined
                                }
                              >
                                {formatCurrency(r.displayedWage)}
                                {r.wageMixed && (
                                  <span className="ml-1 text-[10px] font-semibold text-violet-700">
                                    mixed
                                  </span>
                                )}
                              </span>
                            )}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatHours(r.reg)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatHours(r.ot)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatHours(r.dt)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatHours(r.hol)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {r.mbpHrs > 0 ? formatHours(r.mbpHrs) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {r.sspHrs > 0 ? formatHours(r.sspHrs) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-800">
                        {formatHours(r.totalHrs)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatCurrency(r.regPay)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatCurrency(r.otPay)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatCurrency(r.dtPay)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatCurrency(r.holPay)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {r.specialPay > 0
                          ? formatCurrency(r.specialPay)
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-900">
                        {formatCurrency(r.totalPay)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-400">
                        <NotAvailable />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 border-t-2 border-slate-200">
                    <td
                      colSpan={3}
                      className="px-3 py-2 font-bold text-slate-900 sticky left-0 bg-slate-100 z-10"
                    >
                      Week Total
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatHours(totals.reg)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatHours(totals.ot)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatHours(totals.dt)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatHours(totals.hol)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {totals.mbpHrs > 0 ? formatHours(totals.mbpHrs) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {totals.sspHrs > 0 ? formatHours(totals.sspHrs) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatHours(totals.totalHrs)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatCurrency(totals.regPay)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatCurrency(totals.otPay)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatCurrency(totals.dtPay)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatCurrency(totals.holPay)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {totals.specialPay > 0
                        ? formatCurrency(totals.specialPay)
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900">
                      {formatCurrency(totals.totalPay)}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-400">
                      <NotAvailable />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </PrebuiltReportShell>
  );
}
